import { AppThunkAction } from '../../reducers';
import { QueueDisplayScreen, RegisterDisplayRequest } from '../../../models/Entities/Turnero';
import { ErrorType } from '../../../models/HttpError';

// services
import * as Services from '../../../services/Turnero-QueueDisplayScreen.Services';

// actions
import * as ActionEvents from '../../actions/Turnero-QueueDisplayScreen.Actions';
import {
    TurneroQueueDisplayScreenGetAllFailureAction,
    TurneroQueueDisplayScreenGetAllRequestAction,
    TurneroQueueDisplayScreenGetAllSuccessAction,
    TurneroQueueDisplayScreenGetByIdFailureAction,
    TurneroQueueDisplayScreenGetByIdRequestAction,
    TurneroQueueDisplayScreenGetByIdSuccessAction,
    TurneroQueueDisplayScreenCreateFailureAction,
    TurneroQueueDisplayScreenCreateRequestAction,
    TurneroQueueDisplayScreenCreateSuccessAction,
    TurneroQueueDisplayScreenUpdateFailureAction,
    TurneroQueueDisplayScreenUpdateRequestAction,
    TurneroQueueDisplayScreenUpdateSuccessAction,
    TurneroQueueDisplayScreenDeleteFailureAction,
    TurneroQueueDisplayScreenDeleteRequestAction,
    TurneroQueueDisplayScreenDeleteSuccessAction,
    TurneroQueueDisplayScreenRegisterFailureAction,
    TurneroQueueDisplayScreenRegisterRequestAction,
    TurneroQueueDisplayScreenRegisterSuccessAction,
} from '../../actions/Turnero-QueueDisplayScreen.Actions';

type KnownAction =
    | TurneroQueueDisplayScreenGetAllRequestAction
    | TurneroQueueDisplayScreenGetAllSuccessAction
    | TurneroQueueDisplayScreenGetAllFailureAction
    | TurneroQueueDisplayScreenGetByIdRequestAction
    | TurneroQueueDisplayScreenGetByIdSuccessAction
    | TurneroQueueDisplayScreenGetByIdFailureAction
    | TurneroQueueDisplayScreenCreateRequestAction
    | TurneroQueueDisplayScreenCreateSuccessAction
    | TurneroQueueDisplayScreenCreateFailureAction
    | TurneroQueueDisplayScreenUpdateRequestAction
    | TurneroQueueDisplayScreenUpdateSuccessAction
    | TurneroQueueDisplayScreenUpdateFailureAction
    | TurneroQueueDisplayScreenDeleteRequestAction
    | TurneroQueueDisplayScreenDeleteSuccessAction
    | TurneroQueueDisplayScreenDeleteFailureAction
    | TurneroQueueDisplayScreenRegisterRequestAction
    | TurneroQueueDisplayScreenRegisterSuccessAction
    | TurneroQueueDisplayScreenRegisterFailureAction;

const ensureToken = (dispatch: any, token: string | null, failureType: string, extra?: Record<string, any>) => {
    if (token === null) {
        dispatch({
            type: failureType,
            ...extra,
            error: {
                ErrorCode: 401,
                ErrorMessage: 'Not authorized',
                Errors: []
            }
        });
        return false;
    }
    return true;
};

export const actionCreators = {
    GetQueueDisplayScreens: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!ensureToken(dispatch, token, ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_GET_ALL_FAILURE)) return;

        dispatch({ type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_GET_ALL_REQUEST });

        Services.GetQueueDisplayScreens(token!)
            .then((response) => {
                dispatch({
                    type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_GET_ALL_SUCCESS,
                    payload: response
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_GET_ALL_FAILURE,
                    error
                });
            });
    },

    GetQueueDisplayScreenById: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!ensureToken(dispatch, token, ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_GET_BY_ID_FAILURE, { id })) return;

        dispatch({ type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_GET_BY_ID_REQUEST, id });

        Services.GetQueueDisplayScreenById(token!, id)
            .then((response) => {
                dispatch({
                    type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_GET_BY_ID_SUCCESS,
                    payload: response
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_GET_BY_ID_FAILURE,
                    id,
                    error
                });
            });
    },

    CreateQueueDisplayScreen: (payload: QueueDisplayScreen): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!ensureToken(dispatch, token, ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_CREATE_FAILURE)) return;

        dispatch({ type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_CREATE_REQUEST, payload });

        Services.CreateQueueDisplayScreen(token!, payload)
            .then((response) => {
                dispatch({
                    type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_CREATE_SUCCESS,
                    payload: response
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_CREATE_FAILURE,
                    error
                });
            });
    },

    UpdateQueueDisplayScreen: (id: number, payload: QueueDisplayScreen): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!ensureToken(dispatch, token, ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_UPDATE_FAILURE, { id })) return;

        dispatch({ type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_UPDATE_REQUEST, id, payload });

        Services.UpdateQueueDisplayScreen(token!, id, payload)
            .then((response) => {
                dispatch({
                    type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_UPDATE_SUCCESS,
                    id,
                    payload: response,
                    display: { ...payload, id }
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_UPDATE_FAILURE,
                    id,
                    error
                });
            });
    },

    DeleteQueueDisplayScreen: (id: number): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!ensureToken(dispatch, token, ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_DELETE_FAILURE, { id })) return;

        dispatch({ type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_DELETE_REQUEST, id });

        Services.DeleteQueueDisplayScreen(token!, id)
            .then(() => {
                dispatch({
                    type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_DELETE_SUCCESS,
                    payload: id
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_DELETE_FAILURE,
                    id,
                    error
                });
            });
    },

    RegisterQueueDisplayScreen: (payload: RegisterDisplayRequest): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

        if (!ensureToken(dispatch, token, ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_REGISTER_FAILURE)) return;

        dispatch({ type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_REGISTER_REQUEST, payload });

        Services.RegisterQueueDisplayScreen(token!, payload)
            .then((response) => {
                dispatch({
                    type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_REGISTER_SUCCESS,
                    payload: response
                });
            })
            .catch((error: ErrorType) => {
                dispatch({
                    type: ActionEvents.TURNERO_QUEUE_DISPLAY_SCREEN_REGISTER_FAILURE,
                    error
                });
            });
    },
};
